import APIServer from '@taopix/taopix-core/dist/types/Service/APIServer';
import {ExternalAssetMeta} from '../../Utils/Asset';

export type AlbumRequestPayload = {
  assetservice: string;
  accessToken: string;
  nextPageToken: string;
  serviceCode: string;
};

export type PhotoRequestPayload = {
  assetservice: string;
  accessToken: string;
  folderId: string;
  nextPageToken: string;
  groupCode: string;
  libraryLinkPath: string;
  libraryLinkSearchTerm: string;
  serviceCode: string;
};

export type ServerError = {
  code: number;
  fullMessage: string;
  phrase: string;
};

export type ExternalAssetAlbum = {
  id: string;
  imgSrc: string;
  name: string;
  onSelectAlbum: Function;
  path?: string;
};

export type ExternalAlbumResponse = {
  data: {
    albums: ExternalAssetAlbum[];
    nextPageToken: string;
  };
  error: ServerError;
};

export type ExternalAssetResponse = {
  data: {
    photos: ExternalAssetMeta[];
    nextPageToken: string;
  };
  error: ServerError;
};

export default class ExternalAssetServiceAPI {
  constructor(private server: APIServer) {}

  /**
   * Returns the endpoint for the external service
   *
   * @param   {string}  path
   *
   * @return  {string}
   */
  private getEndpoint = (path: string): string => {
    return `/api/assetservice/${path}`;
  };

  /**
   * Performs a POST request to the external service
   *
   * @param   {string}      path
   * @param   {any<T>}      payload
   *
   * @return  {Promise<T>}
   */
  private post = async <T>(path: string, payload: any): Promise<T> => {
    const {server, getEndpoint} = this;

    return server
      .post<T>(getEndpoint(path), payload, {
        token: 'token',
      })
      .then(response => {
        return response.data;
      });
  };

  /**
   * Returns a list of albums from the external service
   *
   * @param   {AlbumRequestPayload}  payload  [payload description]
   *
   * @return  {Promise<ExternalAlbumResponse>}
   */
  public getAlbums = async (payload: AlbumRequestPayload): Promise<ExternalAlbumResponse> => {
    const {post} = this;

    return post<ExternalAlbumResponse>(`${payload.serviceCode}/folders`, payload);
  };

  /**
   * Returns a list of photos from the external service
   *
   * @param   {PhotoRequestPayload<ExternalAssetResponse>}  payload
   *
   * @return  {Promise<ExternalAssetResponse>}
   */
  public getPhotos = async (payload: PhotoRequestPayload): Promise<ExternalAssetResponse> => {
    const {post} = this;

    return post<ExternalAssetResponse>(`${payload.serviceCode}/photos`, payload);
  };

  /**
   * Performs a HEAD request to the external service to get the file size
   *
   * @param   {ExternalAssetMeta<ExternalAssetMeta>}  payload
   *
   * @return  {Promise<ExternalAssetMeta>}
   */
  public getFileSize = async (payload: ExternalAssetMeta): Promise<ExternalAssetMeta> => {
    const {server} = this;
    //get session storage token
    let theHeaders = {};
    const bearerToken = sessionStorage.getItem('googlePhotosToken') ?? '';

    if (bearerToken !== '') {
      theHeaders = {Authorization: `Bearer ${bearerToken}`};
    }

    if (0 !== payload.hires.filesizebytes) {
      return Promise.resolve({
        ...payload,
        filesizebytes: payload.hires.filesizebytes,
      });
    }
    return server.head(payload.hires.proxysrc, {headers: theHeaders}).then(response => {
      const fileSize = parseInt(response.headers['content-length']);
      return {
        ...payload,
        hires: {
          ...payload.hires,
          filesizebytes: fileSize,
        },
        filesizebytes: fileSize,
        filedatemodified: new Date(response.headers['last-modified']).getTime() / 100,
      };
    });
  };

  /**
   * Get the filesize of a batch of images
   *
   * @param   {ExternalAssetMeta[]}  payload  [payload description]
   *
   * @return  {[]}
   */
  public getBatchedFileSize = async (payload: ExternalAssetMeta[]) => {
    return await Promise.all(
      payload.map(asset => {
        return this.getFileSize(asset);
      })
    );
  };

  /**
   * Revoke Access Token
   *
   */
  public revokeToken = async (payload: {
    serviceCode: string;
    accessToken: string;
  }): Promise<{token: string}> => {
    const {post} = this;

    return post<{token: string}>(`${payload.serviceCode}/revokeAccessToken`, payload);
  };
}
